import {
  alpha,
  Box,
  Button,
  IconButton,
  styled,
  SvgIcon,
  SvgIconProps,
  Tab,
  TableCell,
  TableContainer,
  Tabs,
  TabsProps,
  TablePagination,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

export const StyledTableCell = styled(Box)({
  fontFamily: 'Poppins-Medium',
  display: 'flex',
  alignItems: 'center',
});

export const PerformanceTableCell = styled(TableCell)(({ theme }) => ({
  padding: '16px 20px',
  borderBottom: `1px solid ${alpha(theme.palette.common.black || '#000', 0.08)}`,
}));

export const PerformanceTableContainer = styled(TableContainer)(
  ({ theme }) => ({
    borderRadius: theme.spacing(0.5),
  })
);

export const CustomTablePagination = styled(TablePagination)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
}));

export const PageContainer = styled(Box)(
  () => `
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
`
);

export const StepperFooterContainer = styled(Box)(
  ({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background-color: #FFFFFF;
  flex-shrink: 0;

  /* Phones: wrap the button groups instead of overflowing; buttons grow to
     fill each row so they stay full-size tap targets. */
  ${theme.breakpoints.down('sm')} {
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px;

    & > *, & > * > .MuiButton-root, & > .MuiBox-root > * {
      flex: 1 1 auto;
    }
    & > .MuiBox-root {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }
  }
`
);

export const StepperOutlinedButton = styled(Button)(
  () => `
  border-color: #E0E0E0;
  color: #00000099;
  text-transform: none;
  height: 40px;
`
);

export const StepperContainedButton = styled(Button)(
  () => `
  text-transform: none;
  height: 40px;
`
);

export const IconActionButton = styled(Button)(
  () => `
  border-color: #E0E0E0;
  color: #00000099;
  min-width: 40px;
  width: 40px;
  height: 40px;
  padding: 0;
`
);

export const DashedDivider = styled(Box)(
  () => `
  width: 100%;
  height: 2px;
  background-image: repeating-linear-gradient(to right, #EFECEC 0 2px, transparent 2px 4px);
`
);

/** The solid counterpart of DashedDivider — same hairline, unbroken. */
export const SectionDivider = styled(Box)(
  () => `
  width: 100%;
  height: 2px;
  background-color: #EFECEC;
`
);

export const CalculationTableContainer = styled(TableContainer)(
  ({ theme }) => ({
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${alpha(theme.palette.common.black || '#000', 0.08)}`,
    borderBottom: 'none',
    overflow: 'hidden',
  })
);

export const PageTitleWrapper = styled(Box)(
  ({ theme }) => `
    padding: 12px 16px;
    background-color: ${theme.palette.common.white};
    flex-shrink: 0;
    margin-bottom: 4px;

    & .MuiTypography-root {
      font-weight: 500;
      font-size: 16px;
      color: ${theme.palette.text.secondary};
      font-family: Inter-Medium;
      margin-right: 12px;
    }
  `
);

export const ContentBox = styled(Box)(
  ({ theme }) => `
    padding: 16px 20px;
    flex-grow: 1;
    min-height: 0;
    background-color: ${theme.palette.common.white};
    overflow-y: auto;
    margin-bottom: 4px;
  `
);

export const LedgerContentBox = styled(Box)(
  ({ theme }) => `
    padding: 16px 20px;
    min-height: 0;
    background-color: ${theme.palette.common.white};
    margin-bottom: 4px;
  `
);

export const SidebarMenuItem = styled(IconButton)(
  ({ theme }) => `
    width: 100%;
    border-radius: 8px;
    color: ${theme.colors.alpha.trueWhite[100]};
    background-color: transparent;
    font-size: 14px;
    font-weight: 400;
    gap: 12px;
    justify-content: flex-start;
    padding: 10px 12px;

    .MuiSvgIcon-root {
      font-size: 20px;
      transition: none;
      color: ${theme.colors.alpha.trueWhite[100]};
    }

    &.active,
    &:hover {
      background-color: ${theme.sidebar.menuItemBgActive};
      color: ${theme.colors.alpha.trueWhite[100]};
    }

    &.active .MuiSvgIcon-root,
    &:hover .MuiSvgIcon-root {
      color: ${theme.colors.alpha.trueWhite[100]};
    }
  `
);

export const StyledIconButton = styled(Button)(
  ({ theme }) => `
    min-width: 40px;
    width: 40px;
    max-width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 4px;

    /* 👉 Target THIS button when variant="outlined" */
    &.MuiButton-outlined .MuiSvgIcon-root {
      color: ${alpha(theme.colors.alpha.black[100], 0.4)};
    }

    /* Disabled must not read as "selected": a light grey face and the resting
       faint outline (MUI re-adds a solid border and the theme tints the
       background with primary on .Mui-disabled), with a faded icon. */
    &.MuiButton-outlined.Mui-disabled {
      background-color: ${alpha(theme.colors.alpha.black[100], 0.06)} !important;
      border: none;
      outline: 1px solid ${alpha(theme.colors.alpha.black[100], 0.08)};
    }

    &.MuiButton-outlined.Mui-disabled .MuiSvgIcon-root {
      color: ${alpha(theme.colors.alpha.black[100], 0.2)};
    }

    /* Default icon size */
    & .MuiSvgIcon-root {
      width: 24px;
      height: 24px;
    }
  `
);

export const ThemeSettingsButton = styled(Box)(
  ({ theme }) => `
    position: fixed;
    z-index: 9;
    right: ${theme.spacing(4)};
    bottom: ${theme.spacing(4)};
          
    &::before {
      width: 30px;
      height: 30px;
      content: ' ';
      position: absolute;
      border-radius: 100px;
      left: 13px;
      top: 13px;
      background: ${theme.colors.primary.main};
      animation: ripple 1s infinite;
      transition: ${theme.transitions.create(['all'])};
    }

    .MuiSvgIcon-root {
      animation: pulse 1s infinite;
      transition: ${theme.transitions.create(['all'])};
    }
  `
);

export const ThemeToggleWrapper = styled(Box)(
  ({ theme }) => `
    padding: 0 !important;
    // padding: ${theme.spacing(2)};
    min-width: 220px;
  `
);

export const HeaderWrapper = styled(Box)(
  ({ theme }) => `
    height: ${theme.header.height};
    color: ${theme.header.textColor};
    padding: 12px 20px;
    background-color: ${alpha(theme?.header?.background as string, 0.95)};
    backdrop-filter: blur(3px);
    justify-content: space-between;
    width: 100%;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
  `
);

const StyledTabsWrapper = styled(Tabs)(
  ({ theme }) => `
    padding-block: 6px;
    padding-left: 8px;
    background-color: ${alpha(theme.palette.primary.main, 0.1)};
    border: 1px solid ${theme.palette.divider};
    border-radius: 50px;
    height: 40px;
    border: none;
    /* The strip is a pill group, so it hugs its labels instead of stretching
       to whatever container it sits in. It only ever filled the row below the
       md breakpoint, where the toolbar stacks and stretches its children; on
       desktop it is a flex item and already sized to content. The max-width
       below still caps it, so an overlong set of labels scrolls rather than
       overflowing. */
    width: fit-content;
    max-width: 100%;
    min-width: 0;

    /* Phones: tighter gaps so two long labels still share one line. */
    ${theme.breakpoints.down('sm')} {
      padding-left: 4px;
      padding-right: 4px;
      gap: 4px;
    }
  `
);

/**
 * Pill tab strip. Scrollable by default so labels never clip on narrow
 * screens — scroll buttons only appear when the tabs actually overflow, so
 * the desktop strip renders as before. Callers can still override `variant`.
 */
export const TabsWrapper = (props: TabsProps) => (
  <StyledTabsWrapper
    variant='scrollable'
    scrollButtons='auto'
    allowScrollButtonsMobile
    {...props}
  />
);

export const TabsButton = styled(Tab)(
  ({ theme }) => `
    text-transform: none;
    font-size: 12px;
    min-width: 0;
    font-weight: 400;
    color: ${alpha(theme.palette.common.black, 0.4)};
    background-color: transparent;
    border-radius: 30px;
    border: none;
    min-height: 27px;
    height: 27px;

    &.MuiButtonBase-root {
      padding: 6px 16px;
    }

    &.Mui-selected,
    &.Mui-selected:hover {
      background-color: ${theme.palette.common.white};
      color: ${theme.palette.primary.dark};
      font-weight: 600;
      font-family: 'Inter';
      border: none;
      &.MuiButtonBase-root {
        padding: 6px 16px;
      margin-right: 8px;
      }
    }

    &:hover {
      background-color: ${theme.palette.common.white};
      color: ${theme.palette.primary.main};
      border: none;
      // font-weight: 500;
      // font-family: Inter-Medium;

      &.MuiButtonBase-root {
        padding: 6px 16px;
      }
    }

    /* Phones: trim the generous desktop padding so a pair of long labels
       (e.g. "Employee Personal Ledger" + "My Personal Ledger") fits the row
       without scroll arrows clipping them. */
    ${theme.breakpoints.down('sm')} {
      font-size: 11px;

      &.MuiButtonBase-root,
      &.Mui-selected.MuiButtonBase-root,
      &:hover.MuiButtonBase-root {
        padding: 6px 10px;
        margin-right: 0;
      }
    }
  `
);

/**
 * Module tab strip — the squared, boxed tabs that head a dashboard's sections
 * (Administration Hub's "User List (272) / Bulk Upload (0) / User Group (98)",
 * Personal Dashboard's "Overview / Analytics & Documents"). Distinct from
 * `TabsWrapper`, which is the rounded pill group used for status switches.
 *
 * The theme already defaults every Tabs to scrollable with mobile scroll
 * buttons, so a long set of labels never clips on a phone; the spacing between
 * tabs is the theme's `MuiTab` margin.
 */
export const ModuleTabs = styled(Tabs)({
  minHeight: 40,
  gap: 8,
  minWidth: 0,
  '& .MuiTabs-indicator': { display: 'none' },
});

export const ModuleTab = styled(Tab)(({ theme }) => ({
  borderRadius: '4px',
  textTransform: 'none',
  minHeight: 40,
  padding: '9px 24px',
  fontSize: 14,
  lineHeight: '17px',
  fontWeight: 400,
  color: alpha(theme.palette.common.black, 0.6),
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
  '&.Mui-selected': {
    color: theme.palette.secondary.dark,
    fontWeight: 500,
    backgroundColor: alpha(theme.palette.secondary.main, 0.2),
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
  },
  // Phones: trim the generous desktop padding so three labels still fit
  // without the scroll arrows eating half the strip.
  [theme.breakpoints.down('sm')]: {
    padding: '9px 14px',
    fontSize: 13,
  },
}));

export const StickyTabsContainer = styled(Box)(
  ({ theme }) => `
    width: 100%;
    display: flex;
    justify-content: flex-end;
    position: sticky;
    top: 0;
    z-index: 2;
    padding-block: 8px;
    background-color: ${theme.palette.background.paper};
  `
);

export const SidebarWrapper = styled(Box)(
  ({ theme }) => `
        height: 100%;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 20px 10px;
        background: ${theme.sidebar.background}; /* Reverted to match theme blue */
        transition: width 0.4s ease-in-out;
        overflow: hidden;
`
);
export const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      font-family: 'Inter', ${theme.typography.fontFamily};
      font-weight: 400;
      font-size: 12px;
      line-height: 1;
      letter-spacing: 0px;
      text-transform: none; /* Removing previous uppercase */
      color: ${theme.colors.alpha.trueWhite[50]};
      padding: ${theme.spacing(0, 0, 1.5, 0)}; /* Flushed left, only bottom padding */
      margin-top: ${theme.spacing(2)};
      background: transparent;
    }
`
);

export const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {
      padding: 0;
      .MuiListItem-root {
        padding: 0;
        margin-bottom: 2px;
        min-height: 40px;

        & > .MuiIconButton-root {
          color: ${theme.colors.alpha.trueWhite[100]};
          background-color: transparent; /* Top level gets transparent */
          gap: 12px;
          width: 100%;
          border-radius: 6px;
          border: 1px solid transparent;
          font-weight: ${theme.typography.fontWeightRegular};
          font-size: 14px;
          padding: 10px 14px;
          
          .MuiSvgIcon-root {
             font-size: 18px;
             transition: none;         
             color: ${theme.colors.alpha.trueWhite[100]};
          }

          &.active,
          &:hover {
            background-color: transparent; /* Top level active matches theme background but gets bordered */
            border: 1px solid rgba(255, 255, 255, 0.5);
            color: ${theme.colors.alpha.trueWhite[100]};
            font-weight: 500;

            .MuiSvgIcon-root {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;
        }

        .MuiCollapse-root {
          width: 100%;
          .MuiList-root {
             padding: 4px 6px;
          }

          .MuiListItem-root {
            padding: 0;
            margin-bottom: 2px;
            background: transparent;

            & > .MuiIconButton-root {
              background-color: transparent; /* Clear background for nested by default */
              padding-left: 20px;
              padding-right: 12px;
              padding-top: 8px;
              padding-bottom: 8px;
              font-size: 13px;
              text-align: left;
              border-radius: 6px;
              border: none;
              font-weight: 400;
              position: relative;
              
              &.active,
              &:hover {
                 background-color: ${theme.sidebar.menuItemBgActive}; /* Theme-based nested active background */
                 color: ${theme.colors.alpha.trueWhite[100]};
                 border: none;
                 font-weight: 500;

                 .MuiSvgIcon-root {
                   color: ${theme.colors.alpha.trueWhite[100]};
                 }
              }
            }

            &.depth-2 > .MuiIconButton-root {
              padding-left: 54px; /* Align text under depth 1's text (accounting for its icon) */
            }
          }
        }
      }
    }
`
);

export const SortUpTightIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox='8 9 8 6'>
    <path d='M8.71 12.29 11.3 9.7c.39-.39 1.02-.39 1.41 0l2.59 2.59c.63.63.18 1.71-.71 1.71H9.41c-.89 0-1.33-1.08-.7-1.71' />
  </SvgIcon>
);

export const SortDownTightIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox='8 10 8 6'>
    <path d='M15.29 11.71 12.7 14.3c-.39.39-1.02.39-1.41 0l-2.59-2.59c-.63-.63-.18-1.71.71-1.71h5.18c.89 0 1.33 1.08.7 1.71' />
  </SvgIcon>
);

export const SegmentedGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  background: '#FBFBFD',
  border: '1px solid #E9EDF4',
  borderRadius: '4px',
  padding: '6px 8px',
  gap: '8px',
  height: 44,
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  flexWrap: 'nowrap',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
});

interface StyledButtonProps {
  active?: boolean;
}

export const SegmentedButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'active',
})<StyledButtonProps>(({ theme, active }) => ({
  flexShrink: 0,
  textTransform: 'none',
  fontSize: '12px',
  fontWeight: active ? 500 : 400,
  borderRadius: '4px',
  padding: '8px 16px',
  height: '31px',
  minWidth: '90px',
  whiteSpace: 'nowrap',
  color: active ? theme.palette.secondary.main : 'rgba(0, 0, 0, 0.60)',
  backgroundColor: active
    ? alpha(theme.palette.secondary.main, 0.2)
    : 'transparent',
  border: active
    ? `1px solid ${theme.palette.secondary.main}`
    : '1px solid transparent',
  '&:hover': {
    backgroundColor: active
      ? alpha(theme.palette.secondary.main, 0.25)
      : 'rgba(0, 0, 0, 0.04)',
    borderColor: active ? theme.palette.secondary.main : 'transparent',
  },
}));

export const breadcrumbLinkStyles = {
  margin: 0,
  fontFamily: 'Inter, sans-serif !important',
  fontSize: '12px !important',
  fontStyle: 'normal !important',
  fontWeight: '400 !important',
  lineHeight: '14px !important',
  color: 'var(--Medium-Emphasis, rgba(0, 0, 0, 0.60)) !important',
  display: 'inline-flex',
  alignItems: 'center',
  transition: 'color 0.2s ease-in-out',
  '&:hover': {
    color: 'primary.main !important',
  },
};

export const breadcrumbActiveStyles = {
  margin: 0,
  fontFamily: 'Inter, sans-serif !important',
  fontSize: '12px !important',
  fontStyle: 'normal !important',
  fontWeight: '500 !important',
  lineHeight: '14px !important',
  color: 'var(--Hign-Emphasis, rgba(0, 0, 0, 0.80)) !important',
  fontFeatureSettings: "'dlig' on !important",
  display: 'inline-flex',
  alignItems: 'center',
};

export const breadcrumbsStyles = {
  '& .MuiBreadcrumbs-separator': {
    mx: 0.75,
  },
  '& .MuiTypography-root': {
    marginRight: '0 !important',
  },
};

export const breadcrumbSeparatorIconStyles = {
  color: 'rgba(0, 0, 0, 0.2)',
  fontSize: 16,
};

// ─── Batch Details & Common ───────────────────────────────────────────

export const StepContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3.5),
}));

export const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

export const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2),
}));

export const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  gap: theme.spacing(2),
}));

export const SmallHeaderChip = styled(Chip)(() => ({
  fontWeight: 400,
  fontSize: '12px',
  height: '22px',
  borderRadius: '5px',
  backgroundColor: '#fef1fc',
  color: '#b64aab',
  '&.MuiChip-colorInfo': {
    backgroundColor: '#fef1fc',
    color: '#b64aab',
  },
}));

export const UploadZoneContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'isDragging',
})<{ isDragging?: boolean }>(({ theme, isDragging }) => ({
  borderRadius: theme.spacing(1),
  backgroundColor: isDragging
    ? 'rgba(74,131,182,0.1)'
    : 'rgba(74,131,182,0.05)',
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  gap: theme.spacing(1.5),
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: 'rgba(74,131,182,0.1)',
  },
}));

export const StyledDocumentCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'isChecked' && prop !== 'isCheckable',
})<{ isChecked?: boolean; isCheckable?: boolean }>(({ isCheckable }) => ({
  padding: '16px',
  border: 'none',
  borderRadius: '8px',
  backgroundColor: '#FAFAFA',
  display: 'flex',
  justifyContent: 'space-between',
  gap: '12px',
  position: 'relative',
  cursor: isCheckable ? 'pointer' : 'default',
  height: 64,
}));
